/**
 * LinkEmailModal - Modal for wallet users to link their email address.
 * 
 * This enables wallet users to also login via email on mobile apps,
 * accessing the same account and balances.
 * 
 * Flow:
 * 1. User clicks "Add Email for Mobile Access" in the user dropdown
 * 2. Modal opens, explaining the benefits
 * 3. User clicks "Link Email" which opens Privy's email login (handles OTP)
 * 4. After Privy verifies email, we call backend to store the linkage
 * 5. User can now login with email on mobile
 */

import { useState, useCallback } from "react";
import { useLogin, usePrivy } from "@privy-io/react-auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Mail,
  Smartphone,
  CheckCircle2,
  Loader2Icon,
  AlertCircleIcon,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { 
  getStoredToken, 
  getStoredWallet, 
  getStoredUserId, 
  getTokenExpiry,
  storeToken 
} from "@/backend/auth";

import { API_BASE_URL } from "@/backend/config";

interface LinkEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Backend JWT token for authenticated API calls */
  backendToken: string | null;
  /** Callback when email is successfully linked */
  onSuccess?: () => void;
}

export function LinkEmailModal({ 
  open, 
  onOpenChange, 
  backendToken,
  onSuccess 
}: LinkEmailModalProps) {
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [linkedEmail, setLinkedEmail] = useState<string | null>(null);
  
  const { getAccessToken } = usePrivy();
  
  // Use Privy's login hook to verify email
  // This handles the entire OTP flow - Privy sends the code, user verifies
  const { login } = useLogin({
    onComplete: async ({ user }) => {
      // Only process if the modal is actually open (user initiated email linking)
      // This prevents interference with normal Privy email login flow
      if (!open) {
        return;
      }
      
      // User completed Privy email verification
      try {
        setIsLinking(true);
        setError(null);
        
        // Get the verified email from Privy user
        const email = user.email?.address;
        if (!email) {
          throw new Error("No email found after verification");
        }
        
        // Get Privy access token to prove email ownership
        const privyToken = await getAccessToken();
        if (!privyToken) {
          throw new Error("Failed to get verification token");
        }
        
        // Call backend to link the verified email to wallet account
        const response = await fetch(`${API_BASE_URL}/api/auth/link-email`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${backendToken}`,
          },
          body: JSON.stringify({
            email,
            privy_token: privyToken,
          }),
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: "Failed to link email" }));
          throw new Error(errorData.error || "Failed to link email");
        }
        
        // Success! Update localStorage with the linked email
        const storedToken = getStoredToken();
        const storedWallet = getStoredWallet();
        const storedUserId = getStoredUserId();
        const expiresAt = getTokenExpiry();
        
        if (storedToken && storedWallet && expiresAt) {
          // Re-store token with the new email
          storeToken(storedToken, expiresAt, storedWallet, storedUserId || undefined, email);
        }
        
        setSuccess(true);
        setLinkedEmail(email);
        toast.success("Email linked successfully!", {
          description: "You can now login with this email on mobile.",
        });
        
        onSuccess?.();
        
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to link email";
        setError(message);
        toast.error("Failed to link email", { description: message });
      } finally {
        setIsLinking(false);
      }
    },
    onError: (error) => {
      // Only show error if modal is open (user initiated email linking)
      if (!open) {
        return;
      }
      console.error("Privy login error:", error);
      setError("Email verification failed. Please try again.");
      setIsLinking(false);
    },
  });
  
  // Handle link email button click
  const handleLinkEmail = useCallback(() => {
    setError(null);
    setIsLinking(true);
    
    // Open Privy's email login flow
    // This will handle OTP sending and verification
    login({ loginMethods: ["email"] });
  }, [login]);
  
  // Reset state when modal closes
  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      // Reset state after animation
      setTimeout(() => {
        setError(null);
        setIsLinking(false);
        // Don't reset success state - user should see it until they close
      }, 200);
    }
    onOpenChange(isOpen);
  }, [onOpenChange]);
  
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        {!success ? (
          <>
            <DialogHeader className="text-center sm:text-center pb-1">
              <DialogTitle className="text-xl font-bold flex items-center justify-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Add Email for Mobile Access
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground pt-1">
                Link an email address to login on mobile apps
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 pt-2">
              {/* Single benefit card */}
              <div className="flex items-start gap-3 p-3 rounded-lg bg-accent/50">
                <Smartphone className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Connect on iOS & Android</p>
                  <p className="text-xs text-muted-foreground">
                    Log in with your email on mobile apps to access your account
                  </p>
                </div>
              </div>
              
              {/* Error message */}
              {error && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                    <AlertCircleIcon className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                  <p className="text-xs text-center text-muted-foreground">
                    Click below to try with a different email address
                  </p>
                </div>
              )}
              
              {/* Link Email Button */}
              <Button
                onClick={handleLinkEmail}
                disabled={isLinking}
                className="w-full"
                size="lg"
              >
                {isLinking ? (
                  <>
                    <Loader2Icon className="h-4 w-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : error ? (
                  <>
                    Try with different email
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                ) : (
                  <>
                    Link email to your account
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
              
              <p className="text-xs text-center text-muted-foreground">
                You'll verify your email via a one-time code
              </p>
            </div>
          </>
        ) : (
          // Success state
          <>
            <DialogHeader className="text-center sm:text-center pb-2">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              </div>
              <DialogTitle className="text-xl font-bold">
                Email Linked Successfully!
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground pt-2">
                {linkedEmail}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 pt-2">
              <div className="p-4 rounded-lg bg-accent/50 text-center">
                <p className="text-sm">
                  You can now login with this email on the{" "}
                  <span className="font-medium text-primary">Nordex mobile app</span>
                  {" "}to access your account.
                </p>
              </div>
              
              <Button
                onClick={() => handleOpenChange(false)}
                className="w-full"
                variant="outline"
              >
                Done
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

